/**
 * usePanelRegistry — centralised panel coordinator.
 *
 * Mirrors the prototype's `registerPanel()` / `closeAllOtherPanels()` /
 * `maybeSuppressIfOtherPanelOpen()` machinery from shell.js. Each panel
 * declares its "kind" and the registry enforces mutual exclusion rules:
 *
 *   Mutually exclusive group: notifications, prompt-improver
 *     (only one can be open at a time alongside any other group)
 *
 *   Coexists with everything else:
 *     - utility (context / reasoning / activity)
 *     - ai
 *     - teams
 *     - reasoning (standalone)
 *     - ws-chats / project-chats
 *
 * Returns a stable API for opening, closing, and toggling panels. Wraps
 * the existing `usePanels` + `useNavigation` hooks; callers should prefer
 * this hook over dispatching actions directly so the cross-panel rules
 * stay in one place.
 */

import { useCallback } from 'react';
import { usePanels } from '../stores/useAppStore';
import type { UtilityPanelMode } from '../types';

export type PanelKind =
  | 'ai'
  | 'teams'
  | 'reasoning'
  | 'notifications'
  | 'context'
  | 'activity'
  | 'ws-chats'
  | 'project-chats'
  | 'prompt-improver';

const MUTUALLY_EXCLUSIVE: PanelKind[] = ['notifications', 'prompt-improver'];

export interface PanelRegistryAPI {
  isOpen: (kind: PanelKind) => boolean;
  open: (kind: PanelKind) => void;
  close: (kind: PanelKind) => void;
  toggle: (kind: PanelKind) => void;
  closeAll: () => void;
}

export function usePanelRegistry(): PanelRegistryAPI {
  const {
    panels,
    toggleUtilityPanel,
    toggleAIPanel,
    toggleTeamsPanel,
    setUtilityPanelMode,
    toggleReasoningPanel,
    closeAllPanels,
  } = usePanels();

  const utilityMode = panels.utilityPanelMode;

  const isOpen = useCallback(
    (kind: PanelKind): boolean => {
      switch (kind) {
        case 'ai':
          return panels.aiPanelOpen;
        case 'teams':
          return panels.teamsPanelOpen;
        case 'reasoning':
          return panels.reasoningPanelOpen;
        case 'notifications':
          return utilityMode === 'notifications';
        case 'context':
          return utilityMode === 'context';
        case 'activity':
          return utilityMode === 'activity';
        case 'ws-chats':
          return utilityMode === 'ws-chats';
        case 'project-chats':
          return utilityMode === 'project-chats';
        case 'prompt-improver':
          return utilityMode === 'prompt-improver';
      }
    },
    [panels, utilityMode]
  );

  const open = useCallback(
    (kind: PanelKind) => {
      // Mutual-exclusion rule: opening a member of MUTUALLY_EXCLUSIVE closes
      // any other panel; everything else coexists with siblings.
      const isMutex = MUTUALLY_EXCLUSIVE.includes(kind);
      if (isMutex) {
        closeAllPanels();
      }
      // Apply the open.
      const mapToUtility = (m: UtilityPanelMode) => setUtilityPanelMode(m);
      switch (kind) {
        case 'ai':
          toggleAIPanel(true);
          break;
        case 'teams':
          toggleTeamsPanel(true);
          break;
        case 'reasoning':
          toggleReasoningPanel(true);
          break;
        case 'notifications':
          mapToUtility('notifications');
          break;
        case 'context':
          mapToUtility('context');
          break;
        case 'activity':
          mapToUtility('activity');
          break;
        case 'ws-chats':
          mapToUtility('ws-chats');
          break;
        case 'project-chats':
          mapToUtility('project-chats');
          break;
        case 'prompt-improver':
          mapToUtility('prompt-improver');
          break;
      }
    },
    [closeAllPanels, setUtilityPanelMode, toggleAIPanel, toggleTeamsPanel, toggleReasoningPanel]
  );

  const close = useCallback(
    (kind: PanelKind) => {
      switch (kind) {
        case 'ai':
          toggleAIPanel(false);
          return;
        case 'teams':
          toggleTeamsPanel(false);
          return;
        case 'reasoning':
          toggleReasoningPanel(false);
          return;
        case 'notifications':
        case 'context':
        case 'activity':
        case 'ws-chats':
        case 'project-chats':
        case 'prompt-improver':
          if (utilityMode === kind) {
            toggleUtilityPanel(false);
          }
          return;
      }
    },
    [toggleAIPanel, toggleTeamsPanel, toggleReasoningPanel, toggleUtilityPanel, utilityMode]
  );

  const toggle = useCallback(
    (kind: PanelKind) => {
      if (isOpen(kind)) close(kind);
      else open(kind);
    },
    [isOpen, open, close]
  );

  const closeAll = useCallback(() => closeAllPanels(), [closeAllPanels]);

  return { isOpen, open, close, toggle, closeAll };
}

export default usePanelRegistry;
